import React, { useState } from 'react';
import Skills from '../components/Skills';
import Levels from '../components/Levels';
import SkillGroups from '../components/SkillGroups';
import TeamMembers from '../components/TeamMembers';
import SkillLevelDescriptions from '../components/SkillLevelDescriptions';
import Departments from '../components/Departments';
import TabNav from '../components/TabNav/TabNav';
import TabContent from '../components/TabContent/TabContent';
import Button from '../components/Button/Button';
import Modal from '../components/Modal';
import Input from '../components/Input/Input';
import Select from '../components/Select/Select';
import api from '../services/api';

function AdminPage({ skills, skillGroups, levels, departments, reloadAll }) {
  const [activeTab, setActiveTab] = useState('Skills');
  const [modalOpen, setModalOpen] = useState(false);
  const tabs = ['Skills', 'Levels', 'Skill Groups', 'Team Members', 'Skill Level Descriptions', 'Departments'];
  
  // State for new items
  const [newSkill, setNewSkill] = useState({ title: '', skill_group_id: '', department_id: '' });
  const [newLevel, setNewLevel] = useState({ title: '', department_id: '' });
  const [newSkillGroup, setNewSkillGroup] = useState({ title: '', color: 'hsl(210, 50%, 95%)', department_id: '' });
  const [newDepartment, setNewDepartment] = useState({ name: '' });
  const [newTeamMember, setNewTeamMember] = useState({ name: '', email: '', picture_url: '', department_id: '' });
  
  // Transform departments for Select component
  const departmentOptions = departments.map(dept => ({
    value: dept.id,
    label: dept.name
  }));
  
  // Transform skill groups for Select component, filtered by department
  const getFilteredSkillGroupOptions = (departmentId) => {
    const filtered = departmentId
      ? skillGroups.filter(group => group.department_id === parseInt(departmentId))
      : skillGroups;
      
    return filtered.map(group => ({
      value: group.id,
      label: group.title
    }));
  };
  
  // Handle opening the add modal
  const handleOpenAddModal = () => {
    // Reset form states based on active tab
    if (activeTab === 'Skills') {
      setNewSkill({ title: '', skill_group_id: '', department_id: departments[0]?.id || '' });
    } else if (activeTab === 'Levels') {
      setNewLevel({ title: '', department_id: departments[0]?.id || '' });
    } else if (activeTab === 'Skill Groups') {
      setNewSkillGroup({ title: '', color: 'hsl(210, 50%, 95%)', department_id: departments[0]?.id || '' });
    } else if (activeTab === 'Departments') {
      setNewDepartment({ name: '' });
    } else if (activeTab === 'Team Members') {
      setNewTeamMember({ name: '', email: '', picture_url: '', department_id: departments[0]?.id || '' });
    }
    
    setModalOpen(true);
  };
  
  // Handle form submissions
  const handleCreateSkill = async () => {
    try {
      await api.createSkill({
        title: newSkill.title,
        skill_group_id: newSkill.skill_group_id ? newSkill.skill_group_id : null,
        department_id: newSkill.department_id
      });
      setModalOpen(false);
      reloadAll();
    } catch (error) {
      console.error('Error creating skill:', error);
    }
  };
  
  const handleCreateLevel = async () => {
    try {
      await api.createLevel(newLevel);
      setModalOpen(false);
      reloadAll();
    } catch (error) {
      console.error('Error creating level:', error);
    }
  };
  
  const handleCreateSkillGroup = async () => {
    try {
      await api.createSkillGroup(newSkillGroup);
      setModalOpen(false);
      reloadAll();
    } catch (error) {
      console.error('Error creating skill group:', error);
    }
  };
  
  const handleCreateDepartment = async () => {
    try {
      await api.createDepartment(newDepartment);
      setModalOpen(false);
      reloadAll();
    } catch (error) {
      console.error('Error creating department:', error);
    }
  };
  
  const handleCreateTeamMember = async () => {
    try {
      await api.createTeamMember(newTeamMember);
      setModalOpen(false);
      reloadAll();
    } catch (error) {
      console.error('Error creating team member:', error);
    }
  };
  
  // Render the modal content based on the active tab
  const renderModalContent = () => {
    switch (activeTab) {
      case 'Skills':
        return (
          <div>
            <div className="form-group">
              <Input
                label="Skill Title"
                type="text"
                value={newSkill.title}
                onChange={(e) => setNewSkill({ ...newSkill, title: e.target.value })}
                required
                id="new-skill-title"
              />
            </div>
            <div className="form-group">
              <Select
                label="Skill Group"
                options={getFilteredSkillGroupOptions(newSkill.department_id)}
                value={newSkill.skill_group_id}
                onChange={(value) => setNewSkill({ ...newSkill, skill_group_id: value })}
                placeholder="Select a skill group"
                id="new-skill-group"
              />
            </div>
            <div className="form-group">
              <Select
                label="Department"
                options={departmentOptions}
                value={newSkill.department_id}
                onChange={(value) => setNewSkill({ ...newSkill, department_id: value })}
                placeholder="Select a department"
                id="new-skill-department"
                required
              />
            </div>
          </div>
        );
      
      case 'Levels':
        return (
          <div>
            <div className="form-group">
              <Input
                label="Level Title"
                type="text"
                value={newLevel.title}
                onChange={(e) => setNewLevel({ ...newLevel, title: e.target.value })}
                required
                id="new-level-title"
              />
            </div>
            <div className="form-group">
              <Select
                label="Department"
                options={departmentOptions}
                value={newLevel.department_id}
                onChange={(value) => setNewLevel({ ...newLevel, department_id: value })}
                placeholder="Select a department"
                id="new-level-department"
                required
              />
            </div>
          </div>
        );
      
      case 'Skill Groups':
        return (
          <div>
            <div className="form-group">
              <Input
                label="Skill Group Title"
                type="text"
                value={newSkillGroup.title}
                onChange={(e) => setNewSkillGroup({ ...newSkillGroup, title: e.target.value })}
                required
                id="new-skill-group-title"
              />
            </div>
            <div className="form-group">
              <label htmlFor="new-color">Color</label>
              <input
                id="new-color"
                type="color"
                value={newSkillGroup.color.startsWith('hsl') 
                  ? hslToHex(newSkillGroup.color) 
                  : newSkillGroup.color}
                onChange={(e) => {
                  const hexColor = e.target.value;
                  const hslColor = hexToHsl(hexColor);
                  setNewSkillGroup({ ...newSkillGroup, color: hslColor });
                }}
                style={{ width: '100%', height: '36px', padding: '0', cursor: 'pointer' }}
              />
            </div>
            <div className="form-group">
              <Select
                label="Department"
                options={departmentOptions}
                value={newSkillGroup.department_id}
                onChange={(value) => setNewSkillGroup({ ...newSkillGroup, department_id: value })}
                placeholder="Select a department"
                id="new-skill-group-department"
                required
              />
            </div>
          </div>
        );
      
      case 'Departments':
        return (
          <div>
            <div className="form-group">
              <Input
                label="Department Name"
                type="text"
                value={newDepartment.name}
                onChange={(e) => setNewDepartment({ name: e.target.value })}
                required
                id="new-department-name"
              />
            </div>
          </div>
        );
      
      case 'Team Members':
        return (
          <div>
            <div className="form-group">
              <Input
                label="Name"
                type="text"
                value={newTeamMember.name}
                onChange={(e) => setNewTeamMember({ ...newTeamMember, name: e.target.value })}
                required
                id="new-member-name"
              />
            </div>
            <div className="form-group">
              <Input
                label="Email"
                type="email"
                value={newTeamMember.email}
                onChange={(e) => setNewTeamMember({ ...newTeamMember, email: e.target.value })}
                id="new-member-email"
              />
            </div>
            <div className="form-group">
              <Input
                label="Profile Picture URL"
                type="text"
                value={newTeamMember.picture_url}
                onChange={(e) => setNewTeamMember({ ...newTeamMember, picture_url: e.target.value })}
                id="new-member-picture"
              />
            </div>
            <div className="form-group">
              <Select
                label="Department"
                options={departmentOptions}
                value={newTeamMember.department_id}
                onChange={(value) => setNewTeamMember({ ...newTeamMember, department_id: value })}
                placeholder="Select a department"
                id="new-member-department"
                required
              />
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };
  
  // Determine the modal title and submit handler based on active tab
  const getModalConfig = () => {
    switch (activeTab) {
      case 'Skills':
        return {
          title: 'Add New Skill',
          onConfirm: handleCreateSkill
        };
      case 'Levels':
        return {
          title: 'Add New Level',
          onConfirm: handleCreateLevel
        };
      case 'Skill Groups':
        return {
          title: 'Add New Skill Group',
          onConfirm: handleCreateSkillGroup
        };
      case 'Departments':
        return {
          title: 'Add New Department',
          onConfirm: handleCreateDepartment
        };
      case 'Team Members':
        return {
          title: 'Add New Team Member',
          onConfirm: handleCreateTeamMember
        };
      default:
        return {
          title: 'Add New Item',
          onConfirm: () => setModalOpen(false)
        };
    }
  };
  
  const modalConfig = getModalConfig();
  
  return (
    <div>
      <TabNav 
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        actions={activeTab !== 'Skill Level Descriptions' && (
          <Button type="secondary" size="small" onClick={handleOpenAddModal}>
            Add {activeTab === 'Team Members' ? 'Member' : activeTab.endsWith('s') ? activeTab.slice(0, -1) : activeTab}
          </Button>
  )}
      />
      <TabContent>
        {activeTab === 'Skills' && (
          <Skills
            key={skillGroups.length}
            skills={skills}
            skillGroups={skillGroups}
            departments={departments}
            reloadAll={reloadAll}
          />
        )}
        {activeTab === 'Levels' && (
          <Levels
            levels={levels}
            departments={departments}
            reloadAll={reloadAll}
          />
        )}
        {activeTab === 'Skill Groups' && (
          <SkillGroups
            skillGroups={skillGroups}
            departments={departments}
            reloadAll={reloadAll}
          />
        )}
        {activeTab === 'Team Members' && (
          <TeamMembers
            skills={skills}
            levels={levels}
            departments={departments}
            reloadAll={reloadAll}
          />
        )}
        {activeTab === 'Skill Level Descriptions' && (
          <SkillLevelDescriptions
            skills={skills}
            levels={levels}
            departments={departments}
            reloadAll={reloadAll}
          />
        )}
        {activeTab === 'Departments' && (
          <Departments
            reloadAll={reloadAll}
          />
        )}
      </TabContent>
      
      {/* Add Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalConfig.title}
        footer={
          <Modal.Actions
            onCancel={() => setModalOpen(false)}
            onConfirm={modalConfig.onConfirm}
            confirmText="Add"
            cancelText="Cancel"
          />
        }
      >
        {renderModalContent()}
      </Modal>
    </div>
  );
}

// Helper function to convert HSL to HEX
function hslToHex(hslStr) {
  // Extract values from hsl string like "hsl(210, 50%, 95%)"
  const match = hslStr.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
  if (!match) return '#e6f0fa'; // Default color if parsing fails
  
  const h = parseInt(match[1], 10) / 360;
  const s = parseInt(match[2], 10) / 100;
  const l = parseInt(match[3], 10) / 100;

  let r, g, b;
  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }

  const toHex = x => {
    const hex = Math.round(x * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

// Helper function to convert HEX to HSL
function hexToHsl(hexStr) {
  // Remove the # if present
  let hex = hexStr.replace('#', '');
  
  // Convert to RGB
  let r = parseInt(hex.substring(0, 2), 16) / 255;
  let g = parseInt(hex.substring(2, 4), 16) / 255;
  let b = parseInt(hex.substring(4, 6), 16) / 255;
  
  // Find min and max
  let max = Math.max(r, g, b);
  let min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    let d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: h = 0;
    }
    h /= 6;
  }

  h = Math.round(h * 360);
  s = Math.round(s * 100);
  l = Math.round(l * 100);

  return `hsl(${h}, ${s}%, ${l}%)`;
}

export default AdminPage; 